import { Button } from '@vibe/core';
import { Close } from '@vibe/icons';
import { useEffect, useState } from 'react';
import { ReleaseAnnouncementSlide } from '@/config/releases';

interface ReleaseAnnouncementModalProps {
  open: boolean;
  announcement: ReleaseAnnouncementSlide | null;
  currentIndex: number;
  totalCount: number;
  isFirst: boolean;
  isLast: boolean;
  isSaving?: boolean;
  onClose: () => void;
  onNext: () => void;
  onPrevious: () => void;
  onSeeAllUpdates: () => void;
}

export const ReleaseAnnouncementModal = ({
  open,
  announcement,
  currentIndex,
  totalCount,
  isFirst,
  isLast,
  isSaving = false,
  onClose,
  onNext,
  onPrevious,
  onSeeAllUpdates,
}: ReleaseAnnouncementModalProps) => {
  const [videoLoaded, setVideoLoaded] = useState(false);

  useEffect(() => {
    setVideoLoaded(false);
  }, [announcement?.videoUrl, open]);

  if (!open || !announcement) return null;

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/60 p-2"
      role="presentation"
    >
      <section
        className="relative z-[9999] w-full max-w-[450px] overflow-hidden rounded-lg bg-white text-primary-neutral shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="release-announcement-title"
        aria-describedby="release-announcement-description"
      >
        <div className="relative bg-black">
          {!videoLoaded && (
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-gray-900 px-4 text-center text-xs text-gray-300">
              Loading video tutorial. If it does not load, you can still read
              the update details below.
            </div>
          )}
          <iframe
            className="relative aspect-video w-full border-0 bg-black"
            src={announcement.videoUrl}
            title={announcement.title}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            loading="lazy"
            onLoad={() => setVideoLoaded(true)}
            referrerPolicy="strict-origin-when-cross-origin"
            sandbox="allow-scripts allow-same-origin allow-presentation"
          />
          <button
            className="absolute right-2 top-2 z-20 flex h-6 w-6 cursor-pointer items-center justify-center rounded-full border-0 bg-white/80 text-gray-700 shadow-md transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Close release announcement"
            onClick={() => onClose()}
            disabled={isSaving}
          >
            <Close className="h-2.5 w-2.5" />
          </button>
        </div>

        <div className="px-4 pb-4 pt-3">
          <div className="mb-2 inline-flex rounded bg-blue-100 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-blue-600">
            {announcement.badgeLabel ?? "WHAT'S NEW IN GETSIGN"}
          </div>

          <h2
            id="release-announcement-title"
            className="m-0 text-base font-semibold leading-tight text-gray-900"
          >
            {announcement.title}
          </h2>
          <p
            id="release-announcement-description"
            className="mt-2 text-xs leading-5 text-gray-600"
          >
            {announcement.description}
          </p>

          <div className="mt-4 flex items-center justify-between gap-2">
            <button
              className="cursor-pointer border-0 bg-transparent p-0 text-[11px] font-medium text-gray-500 underline hover:text-gray-700"
              onClick={onSeeAllUpdates}
              disabled={isSaving}
            >
              See all updates
            </button>

            <div className="flex items-center gap-1">
              {Array.from({ length: totalCount }).map((_, index) => (
                <span
                  key={index}
                  className={
                    index === currentIndex
                      ? 'h-1 w-4 rounded-full bg-blue-500'
                      : 'h-1 w-1 rounded-full bg-gray-200'
                  }
                />
              ))}
            </div>

            <div className="flex items-center gap-2">
              {!isFirst && (
                <Button
                  kind={Button.kinds.SECONDARY}
                  size={Button.sizes.SMALL}
                  onClick={onPrevious}
                  disabled={isSaving}
                >
                  Back
                </Button>
              )}
              <Button
                kind={Button.kinds.PRIMARY}
                size={Button.sizes.SMALL}
                onClick={onNext}
                loading={isSaving}
              >
                {isLast ? 'Got it' : 'Next'}
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
